// src/components/SortBySelect.tsx
// Dropdown for selecting sort field and toggling sort direction.

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { cn } from '@core/ui';

import type { SortOption, SortDirection } from '@/features/workspace/types';

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

function ArrowUpDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 3v18" />
      <path d="M3 7l4-4 4 4" />
      <path d="M17 21V3" />
      <path d="M21 17l-4 4-4-4" />
    </svg>
  );
}

function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function ArrowDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SortBySelectProps {
  sorts: SortOption[];
  activeSort: string | null;
  direction: SortDirection;
  onSortChange: (field: string, direction?: SortDirection) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const SortBySelect = observer(({ sorts, activeSort, direction, onSortChange }: SortBySelectProps) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const activeLabel = activeSort
    ? sorts.find((s) => s.field === activeSort)?.labelKey
    : null;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 300;
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - 200),
      width: 200,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const handleItemClick = (field: string) => {
    if (field === activeSort) {
      // Toggle direction
      onSortChange(field, direction === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(field);
    }
    setOpen(false);
  };

  const DirectionIcon = direction === 'asc' ? ArrowUpIcon : ArrowDownIcon;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
          'border border-border',
          open
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )}
      >
        <ArrowUpDownIcon className="h-4 w-4 flex-shrink-0" />
        <span className="hidden sm:inline">
          {activeLabel ? `${t('workspace.sort.label')}: ${t(activeLabel)}` : t('workspace.sort.label')}
        </span>
        <ChevronDownIcon className={cn('h-3.5 w-3.5 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('workspace.sort.label')}
          style={menuStyle}
          className="z-50 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg p-1"
        >
          {sorts.map((sort) => {
            const isActive = activeSort === sort.field;
            return (
              <button
                key={sort.field}
                role="menuitem"
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-left transition-colors',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-foreground hover:bg-accent/50',
                )}
                onClick={() => handleItemClick(sort.field)}
              >
                <span className="flex-1 truncate">{t(sort.labelKey)}</span>
                {isActive ? (
                  <DirectionIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                ) : (
                  <CheckIcon className="h-3.5 w-3.5 flex-shrink-0 opacity-0" />
                )}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
});

SortBySelect.displayName = 'SortBySelect';
